import clsx from 'clsx';
import { useEffect, useState } from 'react';
import AppRoutes from '@/const/routes';
import { useRouter } from 'next/router';
import { authService } from '@/api';
import { Spinner, Typography } from '@/components/common';
import { VerifyEmailError } from './VerifyEmailError';
import { useUser } from '@/hooks';
import { AuthLayout } from '@/components/layout/layout/AuthLayout';
import { ResetCard } from '@/components/auth/ResetCard';

export const ReactivateAccountCode: React.FC = () => {
	const router = useRouter();
	const { id } = router.query;
	const { user } = useUser();
	const [isLoadingScreen, setIsLoadingScreen] = useState<boolean>(true);
	const [isCheckingUser, setIsCheckingUser] = useState<boolean>(true);
	const [isValidCode, setIsValidCode] = useState<boolean | undefined>(
		undefined
	);
	const { code } = router.query;

	useEffect(() => {
		const fetchValidation = async () => {
			if (!code) return;
			try {
				await authService.verifyRecoverAccountCode(code as string);
				setIsValidCode(true);
			} catch (error: any) {
				setIsValidCode(false);
			} finally {
				setIsLoadingScreen(false);
			}
		};

		fetchValidation();
	}, [code]);

	useEffect(() => {
		if (!router.isReady || isValidCode === undefined) return;
		const fetchUserStatus = async () => {
			try {
				const res = await authService.getUserActiveStatus(id as string);

				if (res.data.activated && user) {
					router.push(
						`${AppRoutes.CLIENTAPP_DASHBOARD}?showUserActiveModal=true`
					);
				}
			} catch (error) {
				console.log(error);
			} finally {
				setTimeout(() => {
					setIsCheckingUser(false);
				}, 500);
			}
		};

		if (id) {
			fetchUserStatus();
		}

		if (isValidCode) {
			setTimeout(() => {
				router.push(
					`${AppRoutes.CLIENTAPP_AUTH_REACTIVATE_ACCOUNT_REQUEST}?code=${code}`
				);
			}, 4000);
		}
	}, [id, user, isValidCode, code, router]);

	const handleBack = () => {
		router.push(AppRoutes.CLIENTAPP_AUTH_SIGN_IN, undefined, { shallow: true });
	};

	const getTitle = () => {
		if (isLoadingScreen || isCheckingUser) {
			return '';
		}

		return isValidCode
			? 'Your Account Has Been Successfully Activated'
			: 'Verification Link Expired';
	};

	return (
		<AuthLayout withCarrousel={false}>
			<ResetCard
				title={getTitle()}
				srcImage={
					isValidCode ? '/img/landing/svg/black-magic-woman-alt.svg' : undefined
				}
				classNameTitle="!text-4xl lg:px-6"
				handleBack={handleBack}
			>
				<>
					<Spinner
						type="loadingPage"
						className={clsx('m-auto mt-10', {
							hidden: !isLoadingScreen && !isCheckingUser,
						})}
					/>

					{!isLoadingScreen && !isCheckingUser && isValidCode && (
						<SuccessScreen />
					)}

					{!isLoadingScreen && !isCheckingUser && !isValidCode && (
						<VerifyEmailError type="reactivate" />
					)}
				</>
			</ResetCard>
		</AuthLayout>
	);
};

const SuccessScreen = () => (
	<>
		<Typography type="regular-18" className="text-center">
			{`Thank you for verifying your account.`}
			<br />
			{`Next, please set your new password.`}
		</Typography>

		<Spinner type="loadingPage" className="m-auto mt-10" />
		<Typography type="regular-16" className="text-center text-gray-35 mt-4">
			Redirecting to the Set your Password page in a few seconds
		</Typography>
	</>
);
